// app/(tabs)/market/gerer-produit/[productId].tsx
//
// Écran d'édition COMPLÈTE d'un produit existant (userpro), enfant de
// gerer/[shopId].tsx : atteint uniquement en cliquant sur une ligne de
// produit dans la section "Produits" de cet écran (router.push avec
// productId en paramètre d'URL — pas de produit transmis directement,
// cet écran charge lui-même ses données via useMyProduct, même principe
// que gerer/[shopId].tsx qui charge sa boutique via useMyShops).
//
// Contrairement à creer-produit.tsx (sections en accordéon, formulaire
// de création), ici TOUTES les sections restent dépliées en permanence :
// on édite du contenu déjà existant, la personne doit voir d'un coup
// d'œil ce qui est déjà renseigné. Sauvegarde EXPLICITE via un bouton
// "Enregistrer" (pas de sauvegarde automatique au blur) — pour que la
// personne sache précisément quand ses changements sont pris en compte.
//
// PATCH V10 : le bouton "Enregistrer les modifications" a été déplacé
// du footer fixe en bas d'écran vers le topHeader, à droite du titre
// (même principe que creer-produit.tsx patch V11). Le footer a été
// supprimé. Le bouton reste piloté par les mêmes règles (isValid,
// saving) et déclenche toujours handleSave. Aucun changement sur le
// flux d'upload d'images : chaque photo continue de se sauvegarder
// immédiatement via son propre appel à updateProduct dans
// ImagePickerField.onUploaded, indépendamment de ce bouton.
//
// PATCH V11 :
//   - Ajout d'un bandeau vert "Modifications enregistrées" affiché 3s
//     après un handleSave réussi (showSavedBanner + timeout nettoyé au
//     démontage et à chaque nouvel enregistrement), pour donner un
//     retour visuel de succès qui manquait jusqu'ici.
//   - L'écran est désormais enveloppé dans un KeyboardAvoidingView
//     (comportement 'padding' sur iOS, comportement par défaut ailleurs)
//     pour empêcher le clavier de pousser/masquer le topHeader — et donc
//     le bouton Enregistrer — quand on édite un champ en bas du
//     formulaire (ex. Prix promotionnel dans Options avancées).
//
// PATCH V12 : le champ "Catégorie" était un FormField texte brut, où la
// personne devait taper un UUID de category_id à la main — inutilisable
// en pratique. Remplacé par CategoryChips (voir
// components/market/CategoryChips.tsx), qui charge les catégories via
// useCategories() (action backend get-categories) et affiche des chips
// horizontaux scrollables. allowClear=false ici : contrairement au
// filtre de recherche de market/index.tsx, un produit doit toujours
// avoir une catégorie choisie (contrainte NOT NULL sur
// market_products.category_id) — pas de chip "Toutes". categoryId reste
// le category_id sélectionné (string), synchronisé avec l'état existant
// et le même bloc `if (categoryId.trim()) fields.category_id = ...` dans
// handleSave, qui n'a pas eu besoin de changer.
//
// PATCH V13 : ajout d'une section "Lien public" (juste après le bandeau
// d'enregistrement, avant "Photos") — logique PUREMENT FRONTEND, aucun
// appel backend. Affiche l'URL publique du produit
// (https://www.harmoniaworld.world/produits/{product.id}), construite à
// partir de product.id déjà chargé. Un tap sur le texte de l'URL navigue
// DANS l'app vers l'écran public correspondant (app/produits/[id].tsx —
// distinct et indépendant de cet écran de gestion). Un bouton "Copier" à
// côté copie l'URL complète via expo-clipboard, avec un feedback visuel
// "Copié !" pendant 2s (linkCopied + timeout nettoyé au démontage). Même
// pattern que gerer/[shopId].tsx (voir ce fichier pour la version
// boutique de cette même fonctionnalité).
//
// Sections :
//   1. Photos            → cover_image_url, image_2_url, image_3_url
//                           (upload direct via ImagePickerField, chacune
//                           sauvegardée immédiatement à l'upload, comme
//                           dans gerer/[shopId].tsx — les images ne
//                           passent pas par le bouton Enregistrer)
//   2. Essentiel          → nom, prix, catégorie, statut (chips)
//   3. Détails            → description courte/longue, marque, état, stock
//   4. Options avancées   → prix promo, devise, mise en avant, boutons
//                           d'action (WhatsApp, Acheter, En savoir plus,
//                           Appeler)
//   5. SEO                → titre et description SEO
//   6. Zone de danger     → suppression définitive (pop-up simple
//                           Confirmer/Annuler — moins critique qu'une
//                           boutique entière, pas de cascade)
//
// Après suppression réussie, retour vers gerer/[shopId].tsx (shop_id
// connu via le produit chargé).
//
// PATCH i18n : tous les textes affichés à l'utilisateur passent par
// react-i18next (namespace market.gererProduit / market.common).
// STATUS_OPTIONS et CONDITIONS sont désormais construits à l'intérieur
// du composant via t(), pour réagir aux changements de langue — même
// principe que gerer/[shopId].tsx.

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  View,
  Text,
  ScrollView,
  ActivityIndicator,
  StyleSheet,
  TouchableOpacity,
  TextInput,
  Switch,
  Modal,
  Platform,
  KeyboardAvoidingView,
} from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import * as Haptics from 'expo-haptics';
import * as Clipboard from 'expo-clipboard';
import { useTranslation } from 'react-i18next';

import { useMyProduct } from '@/hooks/market/useMyProduct';
import { useUpdateProduct, type ProductUpdateFields, type ProductStatus, type ProductCondition } from '@/hooks/market/useUpdateProduct';
import ImagePickerField from '@/components/market/ImagePickerField';
import CategoryChips from '@/components/market/CategoryChips';
import { useScrollAutoHide } from '@/hooks/useScrollAutoHide';
import { useTabBarVisibility } from '@/components/CustomTabBar';

const NATIVE = Platform.OS !== 'web';

const BRAND = '#6610B1';
const BRAND_SOFT = '#F3E8FF';
const BRAND_TINT = '#F9F5FF';
const BORDER = '#E5D9FF';
const INK = '#222';
const MUTED = '#8A8496';
const DANGER = '#DC2626';
const DANGER_SOFT = '#FEF2F2';
const DANGER_BORDER = '#FCA5A5';

// Domaine public — même domaine que le site web public (voir
// app/produits/[id].tsx). Utilisé uniquement pour construire le lien à
// afficher/copier ici ; ne remplace pas les appels API existants.
const PUBLIC_SITE_URL = 'https://www.harmoniaworld.world';

function haptic(style: Haptics.ImpactFeedbackStyle = Haptics.ImpactFeedbackStyle.Light) {
  if (NATIVE) Haptics.impactAsync(style);
}

// ─── Champ texte réutilisable ────────────────────────────────────────────────

function FormField({
  label, value, onChangeText, placeholder, keyboardType, multiline, hint, editable = true,
}: {
  label: string;
  value: string;
  onChangeText: (v: string) => void;
  placeholder?: string;
  keyboardType?: 'default' | 'numeric' | 'email-address' | 'phone-pad' | 'url';
  multiline?: boolean;
  hint?: string;
  editable?: boolean;
}) {
  return (
    <View style={styles.fieldWrap}>
      <Text style={styles.fieldLabel}>{label}</Text>
      <TextInput
        style={[styles.input, multiline && styles.inputMultiline]}
        placeholder={placeholder}
        placeholderTextColor="#B0A8C4"
        value={value}
        onChangeText={onChangeText}
        editable={editable}
        keyboardType={keyboardType === 'phone-pad' && Platform.OS === 'web' ? 'default' : keyboardType}
        multiline={multiline}
        numberOfLines={multiline ? 4 : 1}
        textAlignVertical={multiline ? 'top' : 'center'}
      />
      {!!hint && <Text style={styles.fieldHint}>{hint}</Text>}
    </View>
  );
}

// ─── Switch avec champ conditionnel ──────────────────────────────────────────

function ToggleWithField({
  label, value, onValueChange, fieldValue, onFieldChange, fieldPlaceholder, fieldHint, keyboardType, disabled,
}: {
  label: string;
  value: boolean;
  onValueChange: (v: boolean) => void;
  fieldValue: string;
  onFieldChange: (v: string) => void;
  fieldPlaceholder?: string;
  fieldHint?: string;
  keyboardType?: 'default' | 'phone-pad' | 'url';
  disabled?: boolean;
}) {
  return (
    <View style={styles.toggleBlock}>
      <View style={styles.toggleRow}>
        <Text style={styles.toggleLabel}>{label}</Text>
        <Switch
          value={value}
          onValueChange={onValueChange}
          trackColor={{ false: '#E5E1EC', true: BRAND }}
          thumbColor="#fff"
          disabled={disabled}
        />
      </View>
      {value && (
        <View style={styles.toggleFieldWrap}>
          <TextInput
            style={styles.input}
            placeholder={fieldPlaceholder}
            placeholderTextColor="#B0A8C4"
            value={fieldValue}
            onChangeText={onFieldChange}
            keyboardType={keyboardType === 'phone-pad' && Platform.OS === 'web' ? 'default' : keyboardType}
          />
          {!!fieldHint && <Text style={styles.fieldHint}>{fieldHint}</Text>}
        </View>
      )}
    </View>
  );
}

export default function ManageProductScreen() {
  const { productId } = useLocalSearchParams<{ productId: string }>();
  const router = useRouter();
  const { t } = useTranslation();

  const { product, loading, error, refresh } = useMyProduct(productId);
  const {
    updateProduct, saving, error: saveError,
    deleteProduct, deleting, deleteError,
  } = useUpdateProduct();

  // ── Listes traduites, construites à chaque render (réagissent au
  // changement de langue) plutôt que des constantes statiques. ──
  const STATUS_OPTIONS: { value: ProductStatus; label: string }[] = [
    { value: 'draft',     label: t('market.common.status.draft') },
    { value: 'published', label: t('market.common.status.publishedProduct') },
    { value: 'disabled',  label: t('market.common.status.disabledProduct') },
    { value: 'archived',  label: t('market.common.status.archivedProduct') },
  ];

  const CONDITIONS: { value: ProductCondition; label: string }[] = [
    { value: 'new',         label: t('market.common.conditions.new') },
    { value: 'used',        label: t('market.common.conditions.used') },
    { value: 'refurbished', label: t('market.common.conditions.refurbished') },
  ];

  // ── États locaux (initialisés depuis `product` une fois chargé) ──
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [status, setStatus] = useState<ProductStatus>('draft');

  const [descriptionShort, setDescriptionShort] = useState('');
  const [description, setDescription] = useState('');
  const [brand, setBrand] = useState('');
  const [condition, setCondition] = useState<ProductCondition>('new');
  const [stockQuantity, setStockQuantity] = useState('');

  const [promoPrice, setPromoPrice] = useState('');
  const [currencyCode, setCurrencyCode] = useState('XOF');
  const [isFeatured, setIsFeatured] = useState(false);

  const [showWhatsapp, setShowWhatsapp] = useState(false);
  const [whatsappOverride, setWhatsappOverride] = useState('');
  const [showBuy, setShowBuy] = useState(false);
  const [buyUrl, setBuyUrl] = useState('');
  const [showLearnMore, setShowLearnMore] = useState(false);
  const [learnMoreUrl, setLearnMoreUrl] = useState('');
  const [showCall, setShowCall] = useState(false);
  const [callPhone, setCallPhone] = useState('');
  const [showChat, setShowChat] = useState(true);

  const [seoTitle, setSeoTitle] = useState('');
  const [seoDescription, setSeoDescription] = useState('');

  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  // ── Modale de suppression ──
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);

  // ── Confirmation visuelle après un enregistrement réussi ──
  // Affiche un bandeau temporaire (3s) une fois que updateProduct a
  // retourné true. Le timer précédent est nettoyé à chaque nouvel
  // enregistrement pour éviter qu'un ancien timeout ne masque le
  // bandeau d'un enregistrement plus récent.
  const [showSavedBanner, setShowSavedBanner] = useState(false);
  const savedBannerTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Lien public : feedback "Copié !" temporaire (PATCH V13) ──
  const [linkCopied, setLinkCopied] = useState(false);
  const linkCopiedTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (savedBannerTimeout.current) clearTimeout(savedBannerTimeout.current);
      if (linkCopiedTimeout.current) clearTimeout(linkCopiedTimeout.current);
    };
  }, []);

  // ── AutoScroll + double tap (PATCH V14) ──
  // Même système que DetailProduit.tsx / boutiques/[slug].tsx / gerer/[shopId].tsx :
  // pas de header interne animé ici — le topHeader (bouton retour + titre
  // + bouton Enregistrer) est en dehors du ScrollView et reste toujours
  // visible. Seule la tab bar globale est cachée/montrée au scroll ou au
  // double tap.
  const { setTabBarHidden } = useTabBarVisibility();
  const tabBarHiddenRef = useRef(false);
  const [lastTap, setLastTap] = useState<number | null>(null);

  const showTabBar = useCallback(() => {
    if (tabBarHiddenRef.current) {
      tabBarHiddenRef.current = false;
      setTabBarHidden(false);
    }
  }, [setTabBarHidden]);

  const hideTabBar = useCallback(() => {
    if (!tabBarHiddenRef.current) {
      tabBarHiddenRef.current = true;
      setTabBarHidden(true);
    }
  }, [setTabBarHidden]);

  const { handleScroll } = useScrollAutoHide({
    onShow: showTabBar,
    onHide: hideTabBar,
  });

  const toggleTabBar = useCallback(() => {
    haptic();
    if (tabBarHiddenRef.current) showTabBar();
    else hideTabBar();
  }, [showTabBar, hideTabBar]);

  const handleDoubleTap = useCallback(() => {
    const now = Date.now();
    if (lastTap && now - lastTap < 300) toggleTabBar();
    else setLastTap(now);
  }, [lastTap, toggleTabBar]);


  // Initialise tous les champs locaux à partir du produit chargé — une
  // seule fois (hasLoadedOnce), pour ne pas écraser une saisie en cours
  // si `refresh()` est appelé après un upload d'image.
  useEffect(() => {
    if (product && !hasLoadedOnce) {
      setName(product.name);
      setPrice(String(product.price));
      setCategoryId(product.category_id || '');
      setStatus(product.status);
      setDescriptionShort(product.description_short || '');
      setDescription(product.description || '');
      setBrand(product.brand || '');
      setCondition(product.condition);
      setStockQuantity(String(product.stock_quantity));
      setPromoPrice(product.promo_price != null ? String(product.promo_price) : '');
      setCurrencyCode(product.currency_code || 'XOF');
      setIsFeatured(product.is_featured);
      setShowWhatsapp(product.show_whatsapp_business_button);
      setWhatsappOverride(product.whatsapp_business_phone_override || '');
      setShowBuy(product.show_buy_button);
      setBuyUrl(product.buy_url || '');
      setShowLearnMore(product.show_learn_more_button);
      setLearnMoreUrl(product.learn_more_url || '');
      setShowCall(product.show_call_button);
      setCallPhone(product.call_phone || '');
      setShowChat(product.show_platform_chat_button);
      setSeoTitle(product.seo_title || '');
      setSeoDescription(product.seo_description || '');
      setHasLoadedOnce(true);
    }
  }, [product, hasLoadedOnce]);

  const handleBack = useCallback(() => {
    haptic();
    if (product?.shop_id) {
      router.replace(`/(tabs)/market/gerer/${product.shop_id}`);
    } else {
      router.back();
    }
  }, [router, product]);

  const isValid = useMemo(() => {
    if (!name.trim() || !price.trim()) return false;
    const priceNum = Number(price);
    if (isNaN(priceNum) || priceNum < 0) return false;
    if (promoPrice.trim()) {
      const promoNum = Number(promoPrice);
      if (isNaN(promoNum) || promoNum < 0 || promoNum > priceNum) return false;
    }
    if (showBuy && !buyUrl.trim()) return false;
    if (showLearnMore && !learnMoreUrl.trim()) return false;
    if (showCall && !callPhone.trim()) return false;
    if (showWhatsapp && !whatsappOverride.trim()) return false;
    return true;
  }, [name, price, promoPrice, showBuy, buyUrl, showLearnMore, learnMoreUrl, showCall, callPhone, showWhatsapp, whatsappOverride]);

  const handleSave = useCallback(async () => {
    if (!productId || !isValid || saving) return;
    haptic(Haptics.ImpactFeedbackStyle.Medium);
    setShowSavedBanner(false);

    const fields: ProductUpdateFields = {
      name: name.trim(),
      price: Number(price),
      status,
      condition,
      description_short: descriptionShort.trim() || null,
      description: description.trim() || null,
      brand: brand.trim() || null,
      stock_quantity: stockQuantity.trim() ? Number(stockQuantity) : 0,
      promo_price: promoPrice.trim() ? Number(promoPrice) : null,
      currency_code: currencyCode.trim().toUpperCase() || 'XOF',
      is_featured: isFeatured,
      show_platform_chat_button: showChat,
      show_whatsapp_business_button: showWhatsapp,
      whatsapp_business_phone_override: showWhatsapp ? whatsappOverride.trim() : null,
      show_buy_button: showBuy,
      buy_url: showBuy ? buyUrl.trim() : null,
      show_learn_more_button: showLearnMore,
      learn_more_url: showLearnMore ? learnMoreUrl.trim() : null,
      show_call_button: showCall,
      call_phone: showCall ? callPhone.trim() : null,
      seo_title: seoTitle.trim() || null,
      seo_description: seoDescription.trim() || null,
    };

    if (categoryId.trim()) fields.category_id = categoryId.trim();

    const success = await updateProduct(productId, fields);
    if (success) {
      if (savedBannerTimeout.current) clearTimeout(savedBannerTimeout.current);
      setShowSavedBanner(true);
      savedBannerTimeout.current = setTimeout(() => setShowSavedBanner(false), 3000);
    }
  }, [
    productId, isValid, saving, name, price, status, condition, descriptionShort,
    description, brand, stockQuantity, promoPrice, currencyCode, isFeatured,
    showChat, showWhatsapp, whatsappOverride, showBuy, buyUrl, showLearnMore,
    learnMoreUrl, showCall, callPhone, seoTitle, seoDescription, categoryId,
    updateProduct,
  ]);

  const handleCoverUploaded = useCallback((url: string) => {
    if (!productId) return;
    updateProduct(productId, { cover_image_url: url });
    refresh();
  }, [productId, updateProduct, refresh]);

  const handleImage2Uploaded = useCallback((url: string) => {
    if (!productId) return;
    updateProduct(productId, { image_2_url: url });
    refresh();
  }, [productId, updateProduct, refresh]);

  const handleImage3Uploaded = useCallback((url: string) => {
    if (!productId) return;
    updateProduct(productId, { image_3_url: url });
    refresh();
  }, [productId, updateProduct, refresh]);

  // ── Lien public (PATCH V13) ──
  // Construit uniquement à partir de product.id, déjà chargé — aucun
  // appel réseau supplémentaire.
  const publicProductUrl = useMemo(() => {
    if (!product?.id) return null;
    return `${PUBLIC_SITE_URL}/produits/${product.id}`;
  }, [product?.id]);

  // Tap sur le texte de l'URL : navigue DANS l'app vers l'écran public
  // (app/produits/[id].tsx), pas d'ouverture navigateur — même décision
  // que pour la boutique (voir gerer/[shopId].tsx).
  const handleOpenPublicProduct = useCallback(() => {
    if (!product?.id) return;
    haptic();
    router.push(`/produits/${product.id}`);
  }, [product?.id, router]);

  const handleCopyPublicProductLink = useCallback(async () => {
    if (!publicProductUrl) return;
    haptic();
    await Clipboard.setStringAsync(publicProductUrl);
    if (linkCopiedTimeout.current) clearTimeout(linkCopiedTimeout.current);
    setLinkCopied(true);
    linkCopiedTimeout.current = setTimeout(() => setLinkCopied(false), 2000);
  }, [publicProductUrl]);

  const openDeleteModal = useCallback(() => {
    haptic(Haptics.ImpactFeedbackStyle.Medium);
    setDeleteModalVisible(true);
  }, []);

  const closeDeleteModal = useCallback(() => {
    if (deleting) return;
    setDeleteModalVisible(false);
  }, [deleting]);

  const handleConfirmDelete = useCallback(async () => {
    if (!productId || deleting) return;
    haptic(Haptics.ImpactFeedbackStyle.Heavy);

    const success = await deleteProduct(productId);
    if (success) {
      setDeleteModalVisible(false);
      if (product?.shop_id) {
        router.replace(`/(tabs)/market/gerer/${product.shop_id}`);
      } else {
        router.replace('/(tabs)/market/reglages');
      }
    }
  }, [productId, deleting, deleteProduct, product, router]);

  if (loading) {
    return (
      <View style={styles.centerContainer}>
        <ActivityIndicator size="small" color={BRAND} />
        <Text style={styles.centerText}>{t('market.common.loading')}</Text>
      </View>
    );
  }

  if (error || !product) {
    return (
      <View style={styles.centerContainer}>
        <Ionicons name="alert-circle-outline" size={48} color="#C4B5FD" />
        <Text style={styles.centerText}>
          {error === 'session_expired' ? t('market.common.sessionExpiredReconnect') : t('market.gererProduit.notFound')}
        </Text>
        <TouchableOpacity style={styles.backBtn} onPress={() => router.back()} activeOpacity={0.8}>
          <Text style={styles.backBtnText}>{t('market.common.back')}</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <KeyboardAvoidingView
      style={styles.flexFull}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
    {/* ── Header avec bouton retour + bouton Enregistrer ── */}
    <View style={styles.topHeader}>
      <TouchableOpacity style={styles.topBackBtn} activeOpacity={0.7} onPress={handleBack}>
        <Ionicons name="arrow-back" size={24} color="#333" />
      </TouchableOpacity>
      <Text style={styles.topHeaderTitle} numberOfLines={1}>{product.name}</Text>
      <TouchableOpacity
        style={[styles.headerSaveBtn, (!isValid || saving) && styles.headerSaveBtnDisabled]}
        activeOpacity={0.8}
        onPress={handleSave}
        disabled={!isValid || saving}
      >
        {saving ? (
          <ActivityIndicator size="small" color="#fff" />
        ) : (
          <Text style={styles.headerSaveBtnText}>{t('market.gererProduit.saveButton')}</Text>
        )}
      </TouchableOpacity>
    </View>

    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.content}
      onScroll={handleScroll}
      scrollEventThrottle={16}
      onTouchEnd={handleDoubleTap}
    >

      {(saving) && (
        <View style={styles.savingBanner}>
          <ActivityIndicator size="small" color={BRAND} />
          <Text style={styles.savingBannerText}>{t('market.common.saving')}</Text>
        </View>
      )}
      {!saving && showSavedBanner && (
        <View style={styles.successBanner}>
          <Ionicons name="checkmark-circle" size={16} color="#16A34A" />
          <Text style={styles.successBannerText}>{t('market.gererProduit.savedBanner')}</Text>
        </View>
      )}
      {!!saveError && (
        <View style={styles.errorBanner}>
          <Ionicons name="alert-circle-outline" size={16} color={DANGER} />
          <Text style={styles.errorBannerText}>
            {saveError === 'session_expired' ? t('market.common.sessionExpired') : t('market.gererProduit.saveError')}
          </Text>
        </View>
      )}

      {/* ── Lien public (PATCH V13) ── */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{t('market.gererProduit.sections.publicLink')}</Text>
        <View style={styles.sectionCard}>
          <View style={styles.sectionCardInner}>
            <Text style={styles.fieldHint}>
              {t('market.gererProduit.publicLinkHint')}
            </Text>
            {publicProductUrl ? (
              <>
                <TouchableOpacity
                  style={styles.publicLinkRow}
                  activeOpacity={0.7}
                  onPress={handleOpenPublicProduct}
                >
                  <Ionicons name="link-outline" size={16} color={BRAND} />
                  <Text style={styles.publicLinkText} numberOfLines={1}>{publicProductUrl}</Text>
                  <Ionicons name="chevron-forward" size={16} color={MUTED} />
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.copyLinkBtn}
                  activeOpacity={0.8}
                  onPress={handleCopyPublicProductLink}
                >
                  <Ionicons
                    name={linkCopied ? 'checkmark' : 'copy-outline'}
                    size={16}
                    color={linkCopied ? '#16A34A' : BRAND}
                  />
                  <Text style={[styles.copyLinkBtnText, linkCopied && styles.copyLinkBtnTextSuccess]}>
                    {linkCopied ? t('market.common.copied') : t('market.common.copy')}
                  </Text>
                </TouchableOpacity>
              </>
            ) : (
              <Text style={styles.fieldHint}>{t('market.gererProduit.publicLinkPending')}</Text>
            )}
          </View>
        </View>
      </View>

      {/* ── 1. Photos ── */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{t('market.gererProduit.sections.photos')}</Text>
        <View style={styles.sectionCard}>
          <View style={styles.sectionCardInner}>
            <Text style={styles.fieldLabel}>{t('market.gererProduit.fields.mainPhoto')}</Text>
            <ImagePickerField
              context="product-cover"
              ids={{ shop_id: product.shop_id, product_id: product.id }}
              currentImageUrl={product.cover_image_url}
              onUploaded={handleCoverUploaded}
            />
            <View style={{ height: 16 }} />
            <Text style={styles.fieldLabel}>{t('market.gererProduit.fields.photo2')}</Text>
            <ImagePickerField
              context="product-cover"
              ids={{ shop_id: product.shop_id, product_id: product.id }}
              currentImageUrl={product.image_2_url}
              onUploaded={handleImage2Uploaded}
            />
            <View style={{ height: 16 }} />
            <Text style={styles.fieldLabel}>{t('market.gererProduit.fields.photo3')}</Text>
            <ImagePickerField
              context="product-cover"
              ids={{ shop_id: product.shop_id, product_id: product.id }}
              currentImageUrl={product.image_3_url}
              onUploaded={handleImage3Uploaded}
            />
            <Text style={styles.fieldHint}>{t('market.gererProduit.fields.photo3Hint')}</Text>
          </View>
        </View>
      </View>

      {/* ── 2. Essentiel ── */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{t('market.gererProduit.sections.essential')}</Text>
        <View style={styles.sectionCard}>
          <View style={styles.sectionCardInner}>
            <FormField label={t('market.gererProduit.fields.nameLabel')} value={name} onChangeText={setName} placeholder={t('market.gererProduit.fields.namePlaceholder')} />
            <FormField
              label={t('market.gererProduit.fields.priceLabel', { currency: currencyCode || 'XOF' })}
              value={price}
              onChangeText={setPrice}
              placeholder={t('market.gererProduit.fields.pricePlaceholder')}
              keyboardType="numeric"
            />

            {/* PATCH V12 : remplace l'ancien FormField "Catégorie" (UUID
                tapé à la main) par un vrai sélecteur en chips. */}
            <View style={styles.fieldWrap}>
              <CategoryChips
                label={t('market.gererProduit.fields.categoryLabel')}
                value={categoryId || undefined}
                onValueChange={(id) => setCategoryId(id || '')}
              />
            </View>

            <Text style={styles.fieldLabel}>{t('market.gererProduit.fields.statusLabel')}</Text>
            <View style={styles.chipRow}>
              {STATUS_OPTIONS.map((s) => (
                <TouchableOpacity
                  key={s.value}
                  style={[styles.chip, status === s.value && styles.chipActive]}
                  activeOpacity={0.7}
                  onPress={() => { haptic(); setStatus(s.value); }}
                >
                  <Text style={[styles.chipText, status === s.value && styles.chipTextActive]}>{s.label}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        </View>
      </View>

      {/* ── 3. Détails ── */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{t('market.gererProduit.sections.details')}</Text>
        <View style={styles.sectionCard}>
          <View style={styles.sectionCardInner}>
            <FormField label={t('market.gererProduit.fields.descriptionShortLabel')} value={descriptionShort} onChangeText={setDescriptionShort} placeholder={t('market.gererProduit.fields.descriptionShortPlaceholder')} />
            <FormField label={t('market.gererProduit.fields.descriptionLabel')} value={description} onChangeText={setDescription} placeholder={t('market.gererProduit.fields.descriptionPlaceholder')} multiline />
            <FormField label={t('market.gererProduit.fields.brandLabel')} value={brand} onChangeText={setBrand} placeholder={t('market.gererProduit.fields.brandPlaceholder')} />

            <Text style={styles.fieldLabel}>{t('market.gererProduit.fields.conditionLabel')}</Text>
            <View style={styles.chipRow}>
              {CONDITIONS.map((c) => (
                <TouchableOpacity
                  key={c.value}
                  style={[styles.chip, condition === c.value && styles.chipActive]}
                  activeOpacity={0.7}
                  onPress={() => { haptic(); setCondition(c.value); }}
                >
                  <Text style={[styles.chipText, condition === c.value && styles.chipTextActive]}>{c.label}</Text>
                </TouchableOpacity>
              ))}
            </View>

            <FormField label={t('market.gererProduit.fields.stockLabel')} value={stockQuantity} onChangeText={setStockQuantity} placeholder={t('market.gererProduit.fields.stockPlaceholder')} keyboardType="numeric" />
          </View>
        </View>
      </View>

      {/* ── 4. Options avancées ── */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{t('market.gererProduit.sections.advanced')}</Text>
        <View style={styles.sectionCard}>
          <View style={styles.sectionCardInner}>
            <FormField label={t('market.gererProduit.fields.promoPriceLabel')} value={promoPrice} onChangeText={setPromoPrice} placeholder={t('market.gererProduit.fields.promoPricePlaceholder')} keyboardType="numeric" />
            <FormField label={t('market.gererProduit.fields.currencyLabel')} value={currencyCode} onChangeText={(v) => setCurrencyCode(v.toUpperCase())} placeholder={t('market.gererProduit.fields.currencyPlaceholder')} hint={t('market.gererProduit.fields.currencyHint')} />

            <View style={styles.toggleBlock}>
              <View style={styles.toggleRow}>
                <Text style={styles.toggleLabel}>{t('market.gererProduit.fields.featuredLabel')}</Text>
                <Switch value={isFeatured} onValueChange={setIsFeatured} trackColor={{ false: '#E5E1EC', true: BRAND }} thumbColor="#fff" />
              </View>
            </View>

            <View style={styles.toggleBlock}>
              <View style={styles.toggleRow}>
                <Text style={styles.toggleLabel}>{t('market.gererProduit.fields.showChatLabel')}</Text>
                <Switch value={showChat} onValueChange={setShowChat} trackColor={{ false: '#E5E1EC', true: BRAND }} thumbColor="#fff" />
              </View>
            </View>

            <View style={styles.divider} />
            <Text style={styles.groupLabel}>{t('market.gererProduit.actionButtons.groupLabel')}</Text>

            <ToggleWithField
              label={t('market.gererProduit.actionButtons.whatsapp')}
              value={showWhatsapp}
              onValueChange={setShowWhatsapp}
              fieldValue={whatsappOverride}
              onFieldChange={setWhatsappOverride}
              fieldPlaceholder={t('market.gererProduit.actionButtons.whatsappPlaceholder')}
              fieldHint={t('market.gererProduit.actionButtons.whatsappHint')}
              keyboardType="phone-pad"
            />
            <ToggleWithField
              label={t('market.gererProduit.actionButtons.buy')}
              value={showBuy}
              onValueChange={setShowBuy}
              fieldValue={buyUrl}
              onFieldChange={setBuyUrl}
              fieldPlaceholder="https://…"
              keyboardType="url"
            />
            <ToggleWithField
              label={t('market.gererProduit.actionButtons.learnMore')}
              value={showLearnMore}
              onValueChange={setShowLearnMore}
              fieldValue={learnMoreUrl}
              onFieldChange={setLearnMoreUrl}
              fieldPlaceholder="https://…"
              keyboardType="url"
            />
            <ToggleWithField
              label={t('market.gererProduit.actionButtons.call')}
              value={showCall}
              onValueChange={setShowCall}
              fieldValue={callPhone}
              onFieldChange={setCallPhone}
              fieldPlaceholder={t('market.gererProduit.actionButtons.callPlaceholder')}
              keyboardType="phone-pad"
            />
          </View>
        </View>
      </View>

      {/* ── 5. SEO ── */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{t('market.gererProduit.sections.seo')}</Text>
        <View style={styles.sectionCard}>
          <View style={styles.sectionCardInner}>
            <FormField label={t('market.gererProduit.fields.seoTitleLabel')} value={seoTitle} onChangeText={setSeoTitle} placeholder={t('market.gererProduit.fields.seoTitlePlaceholder')} />
            <FormField label={t('market.gererProduit.fields.seoDescriptionLabel')} value={seoDescription} onChangeText={setSeoDescription} placeholder={t('market.gererProduit.fields.seoDescriptionPlaceholder')} multiline />
          </View>
        </View>
      </View>

      {/* ── 6. Zone de danger ── */}
      <View style={styles.section}>
        <Text style={[styles.sectionTitle, styles.dangerSectionTitle]}>{t('market.gererProduit.sections.dangerZone')}</Text>
        <View style={styles.dangerCard}>
          <Text style={styles.dangerTitle}>{t('market.gererProduit.dangerTitle')}</Text>
          <Text style={styles.dangerText}>{t('market.gererProduit.dangerText')}</Text>
          <TouchableOpacity style={styles.dangerBtn} activeOpacity={0.8} onPress={openDeleteModal}>
            <Ionicons name="trash-outline" size={18} color="#fff" />
            <Text style={styles.dangerBtnText}>{t('market.gererProduit.dangerButton')}</Text>
          </TouchableOpacity>
        </View>
      </View>

      <View style={{ height: 40 }} />
    </ScrollView>

    {/* ── Modale de suppression (confirmation simple) ── */}
    <Modal visible={deleteModalVisible} transparent animationType="fade" onRequestClose={closeDeleteModal}>
      <View style={styles.modalOverlay}>
        <View style={styles.modalCard}>
          <View style={styles.modalIconWrap}>
            <Ionicons name="warning" size={26} color={DANGER} />
          </View>
          <Text style={styles.modalTitle}>{t('market.gererProduit.deleteModal.title', { name: product.name })}</Text>
          <Text style={styles.modalText}>{t('market.gererProduit.deleteModal.text')}</Text>

          {!!deleteError && (
            <Text style={styles.modalErrorText}>
              {deleteError === 'session_expired' ? t('market.common.sessionExpiredReconnect') : t('market.gererProduit.saveError')}
            </Text>
          )}

          <View style={styles.modalActions}>
            <TouchableOpacity style={styles.modalCancelBtn} activeOpacity={0.7} onPress={closeDeleteModal} disabled={deleting}>
              <Text style={styles.modalCancelBtnText}>{t('market.common.cancel')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.modalConfirmBtn, deleting && styles.modalConfirmBtnDisabled]}
              activeOpacity={0.8}
              onPress={handleConfirmDelete}
              disabled={deleting}
            >
              {deleting ? <ActivityIndicator size="small" color="#fff" /> : <Text style={styles.modalConfirmBtnText}>{t('market.gererProduit.deleteModal.confirmButton')}</Text>}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  flexFull: { flex: 1 },
  container: { flex: 1, backgroundColor: '#F0F2F5' },
  content: { padding: 16 },
  topHeader: {
    flexDirection: 'row', alignItems: 'center', paddingHorizontal: 8, paddingVertical: 12,
    backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#F0F0F0',
  },
  topBackBtn: { width: 40, height: 40, justifyContent: 'center', alignItems: 'center' },
  topHeaderTitle: { flex: 1, fontSize: 15, fontWeight: '700', color: '#222', textAlign: 'center', marginHorizontal: 4 },
  headerSaveBtn: {
    backgroundColor: BRAND, borderRadius: 10, paddingHorizontal: 16, paddingVertical: 8,
    minWidth: 90, alignItems: 'center', justifyContent: 'center',
  },
  headerSaveBtnDisabled: { backgroundColor: '#C4B5FD' },
  headerSaveBtnText: { fontSize: 13.5, fontWeight: '700', color: '#fff' },
  centerContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', paddingHorizontal: 32, gap: 12, backgroundColor: '#fff' },
  centerText: { fontSize: 14, color: '#999', textAlign: 'center' },
  backBtn: { marginTop: 8, paddingHorizontal: 20, paddingVertical: 10, backgroundColor: BRAND_SOFT, borderRadius: 10 },
  backBtnText: { fontSize: 14, fontWeight: '700', color: BRAND },
  savingBanner: {
    flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: BRAND_SOFT,
    borderRadius: 10, paddingVertical: 8, paddingHorizontal: 12, marginBottom: 12,
  },
  savingBannerText: { fontSize: 12.5, color: BRAND, fontWeight: '600' },
  successBanner: {
    flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: '#F0FDF4',
    borderRadius: 10, paddingVertical: 8, paddingHorizontal: 12, marginBottom: 12,
  },
  successBannerText: { fontSize: 12.5, color: '#16A34A', fontWeight: '600' },
  errorBanner: {
    flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: '#FEF2F2',
    borderRadius: 10, paddingVertical: 8, paddingHorizontal: 12, marginBottom: 12,
  },
  errorBannerText: { fontSize: 12.5, color: DANGER, fontWeight: '600' },
  section: { marginBottom: 20 },
  sectionTitle: {
    fontSize: 12, fontWeight: '700', color: '#999', textTransform: 'uppercase',
    letterSpacing: 0.5, marginBottom: 8, marginLeft: 4,
  },
  dangerSectionTitle: { color: DANGER },
  sectionCard: {
    backgroundColor: '#fff', borderRadius: 14, overflow: 'hidden',
    shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.06, shadowRadius: 6, elevation: 2,
  },
  sectionCardInner: { paddingHorizontal: 14, paddingVertical: 16 },
  fieldWrap: { marginBottom: 14 },
  fieldLabel: { fontSize: 12.5, fontWeight: '600', color: '#666', marginBottom: 7 },
  fieldHint: { fontSize: 11, color: MUTED, marginTop: 5 },
  input: {
    backgroundColor: BRAND_TINT, borderRadius: 12, borderWidth: 1, borderColor: BORDER,
    paddingHorizontal: 14, paddingVertical: Platform.OS === 'ios' ? 14 : 10, fontSize: 15, color: '#333',
  },
  inputMultiline: { minHeight: 90, paddingTop: 12 },
  chipRow: { flexDirection: 'row', gap: 8, flexWrap: 'wrap', marginBottom: 14 },
  chip: { paddingHorizontal: 14, paddingVertical: 9, borderRadius: 999, backgroundColor: BRAND_TINT, borderWidth: 1, borderColor: BORDER },
  chipActive: { backgroundColor: BRAND, borderColor: BRAND },
  chipText: { fontSize: 13, fontWeight: '600', color: BRAND },
  chipTextActive: { color: '#fff' },
  toggleBlock: { marginBottom: 14 },
  toggleRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  toggleLabel: { fontSize: 13.5, fontWeight: '600', color: INK, flex: 1, marginRight: 12 },
  toggleFieldWrap: { marginTop: 10 },
  divider: { height: 1, backgroundColor: '#F0EDF5', marginVertical: 12 },
  groupLabel: { fontSize: 11, fontWeight: '700', color: MUTED, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 },
  // ── Lien public (PATCH V13) ──
  publicLinkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: BRAND_TINT,
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 12,
    marginTop: 10,
  },
  publicLinkText: {
    flex: 1,
    fontSize: 13,
    fontWeight: '600',
    color: BRAND,
  },
  copyLinkBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    backgroundColor: BRAND_SOFT,
    borderRadius: 12,
    paddingVertical: 11,
    marginTop: 10,
  },
  copyLinkBtnText: {
    fontSize: 13.5,
    fontWeight: '700',
    color: BRAND,
  },
  copyLinkBtnTextSuccess: {
    color: '#16A34A',
  },
  dangerCard: { backgroundColor: DANGER_SOFT, borderRadius: 14, borderWidth: 1, borderColor: DANGER_BORDER, padding: 16 },
  dangerTitle: { fontSize: 14.5, fontWeight: '800', color: DANGER, marginBottom: 6 },
  dangerText: { fontSize: 12.5, color: '#991B1B', lineHeight: 18, marginBottom: 14 },
  dangerBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, backgroundColor: DANGER, borderRadius: 12, paddingVertical: 13 },
  dangerBtnText: { fontSize: 14, fontWeight: '700', color: '#fff' },
  modalOverlay: { flex: 1, backgroundColor: 'rgba(17, 17, 20, 0.55)', justifyContent: 'center', alignItems: 'center', padding: 20 },
  modalCard: { width: '100%', maxWidth: 420, backgroundColor: '#fff', borderRadius: 20, padding: 22 },
  modalIconWrap: { width: 52, height: 52, borderRadius: 16, backgroundColor: DANGER_SOFT, justifyContent: 'center', alignItems: 'center', marginBottom: 14 },
  modalTitle: { fontSize: 17, fontWeight: '800', color: INK, marginBottom: 8 },
  modalText: { fontSize: 13, color: MUTED, lineHeight: 19, marginBottom: 12 },
  modalErrorText: { fontSize: 12, color: DANGER, fontWeight: '600', marginBottom: 8 },
  modalActions: { flexDirection: 'row', gap: 10, marginTop: 12 },
  modalCancelBtn: { flex: 1, paddingVertical: 13, borderRadius: 12, backgroundColor: '#F3F4F6', alignItems: 'center', justifyContent: 'center' },
  modalCancelBtnText: { fontSize: 14, fontWeight: '700', color: '#4B5563' },
  modalConfirmBtn: { flex: 1, paddingVertical: 13, borderRadius: 12, backgroundColor: DANGER, alignItems: 'center', justifyContent: 'center' },
  modalConfirmBtnDisabled: { backgroundColor: '#FCA5A5' },
  modalConfirmBtnText: { fontSize: 14, fontWeight: '700', color: '#fff' },
});
